/* eslint-disable @typescript-eslint/no-unused-expressions */
import fetcher from '@/core/lib/fetcher'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { toast } from '@/core/components/ui/use-toast'
import { Activity, ActivityFormValues } from './activities.types'
import { APIResponse } from '@/core/types/utils'
import { describeActivityError } from './activityErrors'
import { SHOW_TIER_PMS_CODES } from './create/variations/tierFields'
import {
  INHERITED_DETAIL_KEYS,
  LIMITATION_DETAIL_KEYS,
  LIST_FIELDS,
} from './activityFields'

type InheritedDetailKey = Exclude<
  (typeof INHERITED_DETAIL_KEYS)[number],
  LimitationDetailKey
>
type LimitationDetailKey = (typeof LIMITATION_DETAIL_KEYS)[number]

/**
 * Bases only by default. `includeTiers` adds the variation rows (each with
 * `activityDetails.baseActivityId`): Daily Pricing needs them because a tier
 * owns its price curve and never inherits the base's (activity-options, F13).
 * The default key is unchanged; the tiers variant adds a suffix under the
 * same `['activities', siteId]` prefix so the existing invalidations cover
 * both shapes.
 */
export const activitiesQueryOptions = (
  siteId: string,
  token: string,
  { includeTiers = false }: { includeTiers?: boolean } = {}
) =>
  queryOptions({
    queryKey: includeTiers
      ? ['activities', siteId, token, 'tiers']
      : ['activities', siteId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(
          import.meta.env.VITE_API_BOOKING +
            `property/${siteId}/activities` +
            (includeTiers ? '?includeTiers=true' : '')
        )
        .json<Promise<APIResponse<Activity[]>>>(),
  })

type ActivityDetailsForm = ActivityFormValues['activityDetails']
type DetailKey = keyof ActivityDetailsForm

const nullableText = (value: string | null | undefined) =>
  value?.trim() ? value.trim() : null

/**
 * activity-fields keys of `activityDetails`, normalised for the API. A key the
 * form does not hold (undefined: an activity loaded from a property-service
 * without the columns) is left out so the edit never overwrites it; create
 * seeds every key (null / [] / 1) in CreateActivity's defaults.
 */
const activityFieldsPayload = (
  details: ActivityDetailsForm,
  limitations: ActivityFormValues['limitations']
) => {
  const out: Record<string, unknown> = {}
  const text: DetailKey[] = [
    'location',
    'locationUrl',
    'whatToBring',
    'requirements',
    'postBookingInfo',
  ]
  for (const key of text) {
    if (details[key] !== undefined) {
      out[key] = nullableText(details[key] as string | null)
    }
  }
  for (const key of LIST_FIELDS) {
    if (details[key] !== undefined) {
      out[key] = (details[key] ?? [])
        .map((item) => item.trim())
        .filter(Boolean)
    }
  }
  if (details.languages !== undefined) out.languages = details.languages ?? []
  if (details.durationMinutes !== undefined) {
    out.durationMinutes = details.durationMinutes ?? null
  }
  if (limitations.minQuantity !== undefined) {
    // perBooking fixes quantity at 1, so the hidden input never sends more.
    out.minQuantity =
      details.priceBasis === 'perBooking' ? 1 : (limitations.minQuantity ?? 1)
  }
  if (limitations.minAge !== undefined) out.minAge = limitations.minAge ?? null
  if (limitations.maxAge !== undefined) out.maxAge = limitations.maxAge ?? null
  return out
}

/**
 * Activity axes POC: single place that shapes the create/update body.
 * - priceBasis / unitName(Plural) / inventorySourceId and every activity-fields key
 *   travel inside `activityDetails` only: the controller spreads it into the
 *   Activities row and never reads the body root for them. minQuantity /
 *   minAge / maxAge are edited on the Limitations tab (form group
 *   `limitations`) but moved here.
 * - `extras` is sent at the body root only when the form owns it (base
 *   activity). Omitting it (tier dialogs) means "leave extras untouched";
 *   an empty array means "remove all". orderIndex = list position.
 * - a tier (baseActivityId set) inherits the content fields, minQuantity,
 *   ages and the slot priceDelta from its base (F-3); property-service
 *   answers 400 when a variation write carries them, so they are stripped.
 *   Its own activityCode is sent while SHOW_TIER_PMS_CODES.
 * - unit names only mean something under perUnit (D-8); the server blanks
 *   them otherwise, this just keeps the body honest.
 */
export const toActivityPayload = (data: ActivityFormValues) => {
  const { activityCode, unitName, unitNamePlural, ...activityDetails } =
    data.activityDetails
  const priceBasis = activityDetails.priceBasis ?? 'perPerson'
  const isTier = Boolean(activityDetails.baseActivityId)
  const fields = activityFieldsPayload(data.activityDetails, data.limitations)
  const sendCodes = !isTier || SHOW_TIER_PMS_CODES
  const limitations = Object.fromEntries(
    Object.entries(data.limitations).filter(
      ([key]) => !(LIMITATION_DETAIL_KEYS as readonly string[]).includes(key)
    )
  ) as Omit<ActivityFormValues['limitations'], LimitationDetailKey>
  const contentKeys: readonly string[] = INHERITED_DETAIL_KEYS
  const baseDetails = Object.fromEntries(
    Object.entries(activityDetails).filter(([key]) => !contentKeys.includes(key))
  ) as Omit<typeof activityDetails, InheritedDetailKey>
  return {
    ...data,
    limitations,
    activityDetails: {
      ...baseDetails,
      effectiveTime: activityDetails.effectiveTime.map((slot) => {
        const { priceDelta, ...rest } = slot
        if (isTier || priceDelta === undefined) return rest
        return { ...rest, priceDelta: Number(priceDelta) || 0 }
      }),
      ...(isTier ? {} : fields),
      ...(sendCodes && activityCode !== undefined
        ? { activityCode: nullableText(activityCode) }
        : {}),
      priceBasis,
      // Tiers inherit the unit names from their base; property-service 400s
      // on a non-null one, and the tier form carries the base's copy.
      unitName:
        !isTier && priceBasis === 'perUnit' ? nullableText(unitName) : null,
      unitNamePlural:
        !isTier && priceBasis === 'perUnit'
          ? nullableText(unitNamePlural)
          : null,
      inventorySourceId: activityDetails.inventorySourceId || null,
    },
    ...(data.extras
      ? {
          extras: data.extras.map((extra, index) => ({
            ...(extra.id ? { id: extra.id } : {}),
            name: extra.name,
            description: extra.description || null,
            price: extra.price,
            priceBasis: extra.priceBasis,
            extraCode: nullableText(extra.extraCode),
            maxQuantity: extra.maxQuantity ?? null,
            orderIndex: index,
          })),
        }
      : {}),
  }
}

type ActivityFieldsResponse = Partial<
  Pick<ActivityDetailsForm, InheritedDetailKey | 'activityCode'>
> & {
  minQuantity?: number | null
  minAge?: number | null
  maxAge?: number | null
}

/**
 * Inverse of toActivityPayload for the edit form: accepts the axis keys
 * either inside `activityDetails` or at the response root, since the
 * property-service response shape is being extended concurrently. The three
 * Limitations-tab keys are also read from `attendees` / `limitations` for
 * the same reason, and land in the form's `limitations` group.
 */
export const toActivityFormValues = (
  activity: Activity & {
    priceBasis?: ActivityFormValues['activityDetails']['priceBasis']
    unitName?: string | null
    unitNamePlural?: string | null
    inventorySourceId?: string | null
  } & ActivityFieldsResponse
): ActivityFormValues => {
  const details = activity.activityDetails as ActivityDetailsForm &
    ActivityFieldsResponse
  const attendees = activity.attendees as ActivityFormValues['attendees'] & {
    minQuantity?: number | null
  }
  // First placement that carries the key; null counts (it means "not set").
  const pick = (key: LimitationDetailKey) =>
    [
      details[key],
      activity[key],
      activity.limitations[key],
      key === 'minQuantity' ? attendees.minQuantity : undefined,
    ].find((value) => value !== undefined)
  const minQuantity = pick('minQuantity')
  const minAge = pick('minAge')
  const maxAge = pick('maxAge')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { minQuantity: _mq, minAge: _mn, maxAge: _mx, ...detailsRest } = details
  return {
    ...activity,
    activityDetails: {
      ...detailsRest,
      priceBasis: details.priceBasis ?? activity.priceBasis ?? 'perPerson',
      unitName: details.unitName ?? activity.unitName ?? null,
      unitNamePlural: details.unitNamePlural ?? activity.unitNamePlural ?? null,
      inventorySourceId:
        details.inventorySourceId ?? activity.inventorySourceId ?? null,
    },
    limitations: {
      ...activity.limitations,
      // Present on read since activity-fields (null / 1); undefined only
      // against an older property-service, where the payload omits them.
      ...(minQuantity !== undefined ? { minQuantity } : {}),
      ...(minAge !== undefined ? { minAge } : {}),
      ...(maxAge !== undefined ? { maxAge } : {}),
    },
    extras: [...(activity.extras ?? [])].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
    ),
  }
}

export const createActivity = (
  activity: ActivityFormValues,
  siteId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/activities`)
    .post(toActivityPayload(activity))
    .json()

export const activityByIdQueryOptions = (siteId: string, id: string, token: string) =>
  queryOptions({
    queryKey: ['activity', siteId, id, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `property/${siteId}/activities/${id}`)
        .json<Promise<APIResponse<Activity>>>(),
  })

export const useDeleteActivity = (token: string, activityId?: string, siteId?: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/activities/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['activities'],
      })
      activityId &&
        void queryClient.invalidateQueries({
          queryKey: ['activity', siteId, activityId],
        })
    },
    // Archiving an inventory source with active consumers is rejected (409
    // activity_has_dependents); without this the archive just silently
    // failed from the list and card menus.
    onError: (error) => {
      toast({
        title: 'Error',
        description: describeActivityError(error, intl.formatMessage),
        variant: 'destructive',
      })
    },
  })
}

export const updateActivity = (
  data: ActivityFormValues,
  activityId: string,
  siteId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/activities/${activityId}`)
    .put(toActivityPayload(data))
    .json()
